import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { LoadScript, GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  Text,
  VStack,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  IconButton,
  Badge,
  Avatar,
  useColorModeValue,
} from '@chakra-ui/react';
import { SearchIcon, RepeatIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { FaMapMarkerAlt, FaPhone, FaClock } from 'react-icons/fa';
import { getGoogleMapApiKey } from '../../Utilities/Apikeys';
import { useNavigate } from 'react-router-dom';
import { getTechniciansLocation } from '../../Utilities/networks';
import { formatLastSeen } from '../../Utilities/dateFormat';
import PageShell from '../ui/PageShell';
import DataCard from '../ui/DataCard';

const MAP_HEIGHT = 'calc(100vh - 280px)';
const MIN_MAP_HEIGHT = '520px';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  minHeight: MIN_MAP_HEIGHT,
  borderRadius: '1rem',
};

const DEFAULT_CENTER = { lat: 12.9716, lng: 77.5946 };

function TechnicianRow({ tech, isVisible, onToggle, onFocus, onDetails }) {
  return (
    <Box
      p={3}
      borderRadius="xl"
      border="1px solid"
      borderColor={isVisible ? 'brand.200' : 'slate.100'}
      bg={isVisible ? 'brand.50' : 'white'}
      cursor="pointer"
      transition="all 0.15s"
      _hover={{ borderColor: 'brand.300', boxShadow: 'soft' }}
      onClick={onFocus}
    >
      <HStack align="start" spacing={3}>
        <Avatar size="sm" name={tech.name} bg="brand.500" color="white" />
        <Box flex={1} minW={0}>
          <HStack justify="space-between" align="start">
            <Text fontWeight="600" color="slate.800" noOfLines={1}>
              {tech.name}
            </Text>
            <IconButton
              aria-label={isVisible ? 'Hide on map' : 'Show on map'}
              icon={isVisible ? <ViewIcon /> : <ViewOffIcon />}
              size="xs"
              variant="ghost"
              colorScheme="brand"
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
            />
          </HStack>

          <VStack align="stretch" spacing={1} mt={2}>
            <HStack spacing={2} color="slate.500" fontSize="xs">
              <FaPhone size={10} />
              <Text noOfLines={1}>{tech.phone}</Text>
            </HStack>
            {tech.area && (
              <HStack spacing={2} color="slate.500" fontSize="xs">
                <FaMapMarkerAlt size={10} />
                <Text noOfLines={1}>{tech.area}</Text>
              </HStack>
            )}
            <HStack spacing={2} color="slate.400" fontSize="xs">
              <FaClock size={10} />
              <Text noOfLines={1}>{formatLastSeen(tech.lastSeen)}</Text>
            </HStack>
          </VStack>

          <Button
            size="xs"
            variant="link"
            colorScheme="brand"
            mt={2}
            onClick={(e) => {
              e.stopPropagation();
              onDetails();
            }}
          >
            View history
          </Button>
        </Box>
      </HStack>
    </Box>
  );
}

export default function LiveTracking() {
  const navigate = useNavigate();
  const apiKey = getGoogleMapApiKey();
  const listBg = useColorModeValue('slate.50', 'slate.800');

  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [activeInfo, setActiveInfo] = useState(null);

  const fetchTechniciansLocation = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 15000)
      );

      const response = await Promise.race([
        getTechniciansLocation(),
        timeoutPromise,
      ]);

      if (response.data?.status === 'success') {
        const data = response.data.data || [];
        const latestTechnicians = data
          .filter((item) => {
            const lat = parseFloat(item.latitude);
            const lng = parseFloat(item.longitude);
            return !Number.isNaN(lat) && !Number.isNaN(lng) && lat !== 0 && lng !== 0;
          })
          .map((item) => ({
            id: `tech_${item.technician_id}`,
            technicianId: item.technician_id,
            name: item.name || 'Unknown',
            phone: item.phone || 'N/A',
            area: item.area || 'N/A',
            lat: parseFloat(item.latitude),
            lng: parseFloat(item.longitude),
            lastSeen: item.datetime,
          }));

        setTechnicians(latestTechnicians);
        setLastUpdate(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }));

        if (latestTechnicians.length === 0) {
          setError('No live locations yet. Technicians must be online with location tracking enabled.');
        }
      } else {
        setError(response.data?.message || 'Failed to load technician locations');
      }
    } catch (err) {
      if (err.message === 'Request timeout') {
        setError('Request timed out. Check your connection and refresh.');
      } else if (err.response) {
        setError(err.response.data?.message || 'Server error while loading locations');
      } else if (err.request) {
        setError('Could not reach the server. Check your network connection.');
      } else {
        setError('Failed to load technician locations.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTechniciansLocation();
  }, [fetchTechniciansLocation]);

  useEffect(() => {
    if (technicians.length > 0) {
      setSelected(new Set(technicians.map((t) => t.id)));
      setMapCenter({ lat: technicians[0].lat, lng: technicians[0].lng });
    }
  }, [technicians]);

  useEffect(() => {
    const interval = setInterval(() => fetchTechniciansLocation(true), 30000);
    return () => clearInterval(interval);
  }, [fetchTechniciansLocation]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return technicians;
    return technicians.filter(
      (t) =>
        (t.name || '').toLowerCase().includes(q) ||
        (t.phone || '').toLowerCase().includes(q) ||
        (t.area || '').toLowerCase().includes(q)
    );
  }, [search, technicians]);

  const visibleOnMap = technicians.filter((t) => selected.has(t.id));

  const toggleSelect = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const showAll = useCallback(() => {
    setSelected(new Set(technicians.map((t) => t.id)));
  }, [technicians]);

  const hideAll = useCallback(() => {
    setSelected(new Set());
  }, []);

  const focusTechnician = useCallback((tech) => {
    setActiveInfo(tech);
    setMapCenter({ lat: tech.lat, lng: tech.lng });
  }, []);

  const getMarkerIcon = () => ({
    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
    fillColor: '#0891B2',
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 2,
    scale: 1.4,
    anchor: { x: 12, y: 22 },
  });

  return (
    <Box>
      <PageShell
        title="Live Tracking"
        subtitle="See where your field technicians are right now"
        action={
          <HStack spacing={3}>
            {lastUpdate && (
              <Badge colorScheme="brand" variant="subtle" px={3} py={1} borderRadius="full">
                Updated {lastUpdate}
              </Badge>
            )}
            <Button
              leftIcon={<RepeatIcon />}
              colorScheme="brand"
              size="sm"
              borderRadius="lg"
              isLoading={loading}
              onClick={() => fetchTechniciansLocation()}
            >
              Refresh
            </Button>
          </HStack>
        }
      />

      <HStack spacing={3} mb={4} flexWrap="wrap">
        <Badge px={3} py={1} borderRadius="full" bg="white" border="1px solid" borderColor="slate.200">
          {technicians.length} technicians
        </Badge>
        <Badge px={3} py={1} borderRadius="full" bg="brand.50" color="brand.700" border="1px solid" borderColor="brand.100">
          {visibleOnMap.length} on map
        </Badge>
      </HStack>

      {error && (
        <Alert status="warning" borderRadius="xl" mb={4}>
          <AlertIcon />
          <Text fontSize="sm">{error}</Text>
        </Alert>
      )}

      <Flex
        direction={{ base: 'column', lg: 'row' }}
        gap={4}
        align="stretch"
        minH={MIN_MAP_HEIGHT}
      >
        <DataCard flex={1} p={0} position="relative" overflow="hidden" minH={MIN_MAP_HEIGHT}>
          {loading && technicians.length === 0 && (
            <Flex
              position="absolute"
              inset={0}
              zIndex={2}
              bg="whiteAlpha.800"
              align="center"
              justify="center"
              direction="column"
              gap={3}
            >
              <Spinner size="lg" color="brand.500" thickness="3px" />
              <Text color="slate.600" fontSize="sm">Loading live locations...</Text>
            </Flex>
          )}

          {!apiKey ? (
            <Flex align="center" justify="center" minH={MIN_MAP_HEIGHT} p={6}>
              <Text color="slate.500" textAlign="center">
                Add your Google Maps API key in Utilities/Apikeys.js to enable the map.
              </Text>
            </Flex>
          ) : (
            <Box h={MAP_HEIGHT} minH={MIN_MAP_HEIGHT}>
              <LoadScript googleMapsApiKey={apiKey}>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={12}
                  options={{
                    fullscreenControl: true,
                    streetViewControl: false,
                    mapTypeControl: false,
                  }}
                >
                  {visibleOnMap.map((tech) => (
                    <React.Fragment key={tech.id}>
                      <Marker
                        position={{ lat: tech.lat, lng: tech.lng }}
                        icon={getMarkerIcon()}
                        onClick={() => focusTechnician(tech)}
                      />
                      {activeInfo?.id === tech.id && (
                        <InfoWindow
                          position={{ lat: tech.lat, lng: tech.lng }}
                          onCloseClick={() => setActiveInfo(null)}
                        >
                          <Box minW="180px" p={1}>
                            <Text fontWeight="700" color="slate.800">{tech.name}</Text>
                            <Text fontSize="sm" color="slate.600" mt={1}>{tech.phone}</Text>
                            <Text fontSize="xs" color="slate.500" mt={1}>{tech.area}</Text>
                            <Text fontSize="xs" color="brand.600" mt={2}>
                              {formatLastSeen(tech.lastSeen)}
                            </Text>
                          </Box>
                        </InfoWindow>
                      )}
                    </React.Fragment>
                  ))}
                </GoogleMap>
              </LoadScript>
            </Box>
          )}
        </DataCard>

        <DataCard
          w={{ base: 'full', lg: '360px' }}
          flexShrink={0}
          p={0}
          display="flex"
          flexDirection="column"
          maxH={MAP_HEIGHT}
          minH={{ base: '360px', lg: MIN_MAP_HEIGHT }}
        >
          <Box p={4} borderBottom="1px solid" borderColor="slate.100">
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="slate.400" />
              </InputLeftElement>
              <Input
                placeholder="Search name, phone, area..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                borderRadius="lg"
                bg="white"
              />
            </InputGroup>

            <HStack mt={3} spacing={2}>
              <Button size="xs" variant="outline" borderRadius="full" onClick={showAll}>
                Show all
              </Button>
              <Button size="xs" variant="ghost" borderRadius="full" onClick={hideAll}>
                Hide all
              </Button>
              <Text fontSize="xs" color="slate.500" ml="auto">
                {filtered.length} shown
              </Text>
            </HStack>
          </Box>

          <Box flex={1} overflowY="auto" p={3} bg={listBg}>
            {filtered.length === 0 ? (
              <Flex align="center" justify="center" h="full" minH="200px">
                <Text fontSize="sm" color="slate.500" textAlign="center" px={4}>
                  {technicians.length === 0
                    ? 'No technician locations available yet.'
                    : 'No technicians match your search.'}
                </Text>
              </Flex>
            ) : (
              <VStack align="stretch" spacing={2}>
                {filtered.map((tech) => (
                  <TechnicianRow
                    key={tech.id}
                    tech={tech}
                    isVisible={selected.has(tech.id)}
                    onToggle={() => toggleSelect(tech.id)}
                    onFocus={() => focusTechnician(tech)}
                    onDetails={() => navigate(`/technicians/${tech.id}`, {
                      state: {
                        technician: {
                          name: tech.name,
                          phone: tech.phone,
                          area: tech.area,
                        },
                      },
                    })}
                  />
                ))}
              </VStack>
            )}
          </Box>
        </DataCard>
      </Flex>
    </Box>
  );
}
